import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface TenantSignupProps {
  onSignupSuccess?: () => void;
}

const TenantSignup: React.FC<TenantSignupProps> = ({ onSignupSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!companyName || companyName.trim() === '') {
      setError('Company name is required');
      return;
    }

    if (!phoneNumber || phoneNumber.trim() === '') {
      setError('Phone number is required');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/user/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          isAdmin: false,
          companyName: companyName.trim(),
          phoneNumber: phoneNumber.trim()
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Signup failed');
      }

      setSuccess('Account created successfully! Please wait for admin approval before logging in.');

      // Clear form
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setCompanyName('');
      setPhoneNumber('');

      // Call onSignupSuccess callback if provided, but don't auto-redirect
      if (onSignupSuccess) {
        onSignupSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-10 shadow-2xl backdrop-blur-sm">
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl mb-4 shadow-lg">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">Tenant Signup</h1>
        <p className="text-gray-400 text-sm">Create a new tenant account</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="p-4 bg-red-500/10 backdrop-blur-sm rounded-xl text-red-200 text-sm shadow-lg border-l-4 border-red-500">
            {error}
          </div>
        )}
        {success && (
          <div className="p-4 bg-green-500/10 backdrop-blur-sm rounded-xl text-green-200 text-sm shadow-lg border-l-4 border-green-500">
            {success}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <label htmlFor="companyName" className="text-sm font-medium text-gray-300">
            Company Name
          </label>
          <input
            type="text"
            id="companyName"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
            disabled={loading}
            placeholder="Enter your company name"
            className="px-4 py-3 bg-main-light/50 backdrop-blur-sm rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-light/70 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="email" className="text-sm font-medium text-gray-300">
            Email
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            placeholder="your@email.com"
            className="px-4 py-3 bg-main-light/50 backdrop-blur-sm rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-light/70 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="phoneNumber" className="text-sm font-medium text-gray-300">
            Phone Number
          </label>
          <input
            type="tel"
            id="phoneNumber"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            required
            disabled={loading}
            placeholder="Enter your phone number"
            className="px-4 py-3 bg-main-light/50 backdrop-blur-sm rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-light/70 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="password" className="text-sm font-medium text-gray-300">
            Password
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            placeholder="At least 6 characters"
            minLength={6}
            className="px-4 py-3 bg-main-light/50 backdrop-blur-sm rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-light/70 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-300">
            Confirm Password
          </label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={loading}
            placeholder="Confirm your password"
            minLength={6}
            className="px-4 py-3 bg-main-light/50 backdrop-blur-sm rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-light/70 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          />
        </div>

        <button
          type="submit"
          className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-4 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
          disabled={loading}
        >
          {loading ? 'Creating account...' : 'Sign Up'}
        </button>

        <div className="text-center">
          <p className="text-gray-400 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-purple-400 hover:text-purple-300 font-semibold transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export default TenantSignup;
